import { GitQueryKeys } from '@common/queries/GitQueryKeys';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { QUERY_NOTIFY_PROPS_WITH_FETCHING } from 'frontend/core/src/data/queryConstants';
import { useGraphQlMutation, useGraphQlQuery } from 'frontend/core/src/data/util';
import { useMemo, useState } from 'react';

import { DeleteGitBranchesMutation, GitBranchesTabQuery } from './api';
import {
  BranchTabState,
  DeleteGitBranchesParams,
  DeleteGitBranchesResponse,
  GetGitBranchesTabParams,
  GitBranchesTabResponse,
  isCheckoutSelectable,
  LocalCheckout
} from './types';

const EMPTY_CHECKOUTS = FROZEN_EMPTY_ARRAY as LocalCheckout[];
const EMPTY_SELECTED_IDS = FROZEN_EMPTY_ARRAY as string[];

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Failed to load git branches';
}

export function useBranchTabState(projectId: string): BranchTabState {
  const [selectedIds, setSelectedIds] = useState<string[]>(EMPTY_SELECTED_IDS);
  const queryVariables: GetGitBranchesTabParams = {
    projectUid: projectId,
    includeSizeOnDisk: true
  };

  const { data, error, isFetching, refetch } = useGraphQlQuery<GitBranchesTabResponse, GetGitBranchesTabParams>(
    [...GitQueryKeys.LocalGitCheckouts, projectId],
    GitBranchesTabQuery,
    { notifyOnChangeProps: [...QUERY_NOTIFY_PROPS_WITH_FETCHING] },
    queryVariables
  );

  const { mutateAsync: deleteGitBranches } = useGraphQlMutation<DeleteGitBranchesResponse, DeleteGitBranchesParams>(
    DeleteGitBranchesMutation
  );

  const list = data?.localGitCheckouts;
  const checkouts = list?.checkouts ?? EMPTY_CHECKOUTS;

  const visibleSelectedIds = useMemo(() => {
    const checkoutIds = new Set(checkouts.map((checkout) => checkout.userRepositoryUid));
    return selectedIds.filter((id) => checkoutIds.has(id));
  }, [checkouts, selectedIds]);

  const clearSelection = usePersistentCallback(() => {
    setSelectedIds(EMPTY_SELECTED_IDS);
  });

  const refresh = usePersistentCallback(async () => {
    await refetch();
  });

  const deleteCheckouts = usePersistentCallback(async (variables: DeleteGitBranchesParams) => {
    const result = await deleteGitBranches(variables);
    clearSelection();
    await refetch();
    return result.deleteLocalGitCheckouts;
  });

  const toggleCheckout = usePersistentCallback((userRepositoryUid: string) => {
    setSelectedIds((prev) =>
      prev.includes(userRepositoryUid) ? prev.filter((id) => id !== userRepositoryUid) : [...prev, userRepositoryUid]
    );
  });

  const toggleSelectAll = usePersistentCallback(() => {
    const selectableIds = checkouts.filter(isCheckoutSelectable).map((checkout) => checkout.userRepositoryUid);
    setSelectedIds((prev) => {
      const allSelected = selectableIds.length > 0 && selectableIds.every((id) => prev.includes(id));
      return allSelected ? EMPTY_SELECTED_IDS : selectableIds;
    });
  });

  const storageUsedBytes = useMemo(
    () => checkouts.reduce((sum, checkout) => sum + (checkout.detail?.sizeOnDiskBytes ?? 0), 0),
    [checkouts]
  );

  return useMemo(
    () => ({
      state: {
        summary: {
          totalCount: list?.totalCount,
          unusedCount: list?.unusedCount,
          unusedAfterDays: list?.unusedAfterDays ?? 30,
          storageUsedBytes: list ? storageUsedBytes : undefined
        },
        checkouts,
        selectedIds: visibleSelectedIds,
        loading: isFetching,
        error: error ? getErrorMessage(error) : undefined
      },
      actions: {
        refresh,
        deleteCheckouts,
        toggleCheckout,
        toggleSelectAll,
        clearSelection
      }
    }),
    [
      checkouts,
      list,
      storageUsedBytes,
      visibleSelectedIds,
      isFetching,
      error,
      refresh,
      deleteCheckouts,
      toggleCheckout,
      toggleSelectAll,
      clearSelection
    ]
  );
}
